import React, { useState, useCallback, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Download, Maximize2 } from 'lucide-react';

export const PDFViewer = ({ pages, title, pdfUrl, onClose }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const nextPage = useCallback(() => {
    setCurrentPage(prev => prev < pages.length - 1 ? prev + 1 : prev);
  }, [pages.length]);

  const prevPage = useCallback(() => {
    setCurrentPage(prev => prev > 0 ? prev - 1 : prev);
  }, []);

  const handleDownload = useCallback(() => {
    window.open(pdfUrl, '_blank');
  }, [pdfUrl]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        nextPage();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        prevPage();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [nextPage, prevPage, onClose]);

  return (
    <div 
      className={`fixed inset-0 bg-black/95 z-50 flex items-center justify-center ${
        isFullscreen ? 'p-0' : 'p-4'
      }`}
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-lg overflow-hidden ${
          isFullscreen ? 'w-full h-full' : 'max-w-5xl w-full max-h-[90vh]'
        } flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#29367A] text-white px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold truncate flex-1">{title}</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Download PDF"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              <Maximize2 className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-gray-100 relative flex items-center justify-center p-4">
          <img
            src={pages[currentPage]}
            alt={`Page ${currentPage + 1}`}
            className="max-w-full max-h-full object-contain shadow-2xl"
            style={{ maxHeight: isFullscreen ? '100vh' : 'calc(90vh - 200px)' }}
          />

          {/* Navigation Arrows */}
          {currentPage > 0 && (
            <button
              onClick={prevPage}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
            >
              <ChevronLeft className="w-6 h-6 text-[#29367A]" />
            </button>
          )}
          {currentPage < pages.length - 1 && (
            <button
              onClick={nextPage}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
            >
              <ChevronRight className="w-6 h-6 text-[#29367A]" />
            </button>
          )}
        </div>

        {/* Footer - Page Counter */}
        <div className="bg-gray-800 text-white px-6 py-3 flex items-center justify-between">
          <div className="text-sm">
            Page {currentPage + 1} of {pages.length}
          </div>
          
          {/* Page Dots */}
          <div className="flex gap-2">
            {pages.map((page, index) => (
              <button
                key={`page-dot-${index}-${page}`}
                onClick={() => setCurrentPage(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentPage 
                    ? 'bg-white w-6' 
                    : 'bg-white/30 hover:bg-white/50'
                }`}
                title={`Go to page ${index + 1}`}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>

          <div className="text-sm text-gray-400">
            Use ← → keys to navigate
          </div>
        </div>
      </div>
    </div>
  );
};
